import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Hero from '../common/Hero';
import BusinessCard from '../features/BusinessCard';
import DancingShield from "../common/DancingShield";

// --- Styled Components ---

const BusinessesContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
`;

const Section = styled.section`
    margin: 4rem 0;
`;

const SectionTitle = styled.h2`
    font-size: 2rem;
    margin-bottom: 2rem;
    color: #333;
    font-family: 'Playfair Display', serif;
    position: relative;

    &:after {
        content: '';
        position: absolute;
        bottom: -10px;
        left: 0;
        width: 60px;
        height: 3px;
        background-color: #6c5ce7;
    }
`;

const Introduction = styled.div`
    margin-bottom: 3rem;

    p {
        font-size: 1.1rem;
        line-height: 1.8;
        color: #555;
    }
`;

// New Search Styles
const SearchContainer = styled.div`
    margin-bottom: 2rem;
    display: flex;
    justify-content: center;
    width: 100%;
`;

const SearchInput = styled.input`
    width: 100%;
    max-width: 500px;
    padding: 1rem 1.5rem;
    font-size: 1rem;
    border: 2px solid #e0e0e0;
    border-radius: 50px;
    outline: none;
    transition: all 0.3s ease;
    background-color: #f9f9f9;
    color: #333;

    &:focus {
        border-color: #6c5ce7;
        box-shadow: 0 4px 10px rgba(108, 92, 231, 0.1);
        background-color: #fff;
    }

    &::placeholder {
        color: #aaa;
    }
`;

const BusinessesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 2rem;
`;

const CardWrapper = styled(motion.div)`
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
        transform: translateY(-5px);
    }
`;

const PaginationContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 3rem;
    gap: 1rem;
`;

const PageButton = styled.button`
    padding: 0.5rem 1rem;
    border: 1px solid #6c5ce7;
    background-color: ${props => props.$active ? '#6c5ce7' : 'transparent'};
    color: ${props => props.$active ? '#fff' : '#6c5ce7'};
    border-radius: 4px;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        background-color: #6c5ce7;
        color: #fff;
    }

    &:disabled {
        border-color: #ccc;
        color: #ccc;
        cursor: not-allowed;
    }
`;

const LoadingMessage = styled.div`
    text-align: center;
    padding: 2rem;
    font-size: 1.2rem;
    color: #666;
`;

const ErrorMessage = styled.div`
    text-align: center;
    padding: 2rem;
    color: #e74c3c;
    background: #fdeaea;
    border-radius: 8px;
`;

const NoResultsMessage = styled.div`
    text-align: center;
    padding: 3rem;
    color: #555;
    font-style: italic;
`;

const API_ENDPOINT = 'https://z184yo4b1e.execute-api.eu-west-1.amazonaws.com/prod/mlalazi/businesses';

const FamilyBusinesses = () => {
    // State management
    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 6;

    const navigate = useNavigate();

    // Fetch data from API
    useEffect(() => {
        // Debounce setup to prevent API spamming while typing
        const delayDebounceFn = setTimeout(async () => {
            try {
                setLoading(true);

                // Construct URL with query parameters
                const params = new URLSearchParams({
                    page: currentPage,
                    limit: itemsPerPage,
                });

                // Append search param if it exists
                if (searchQuery.trim()) {
                    params.append('search', searchQuery.trim());
                }

                const response = await fetch(`${API_ENDPOINT}?${params.toString()}`);

                if (!response.ok) {
                    throw new Error('Failed to fetch businesses');
                }

                const data = await response.json();
                console.log(data)

                // Handle data logic (including filtering if backend doesn't support search)
                let fetchedBusinesses = data.data || [];

                // Fallback: If the API doesn't filter server-side, we filter client-side here
                // Note: Ideally the backend handles this to support pagination correctly
                if (searchQuery.trim() && !data.meta?.isFiltered) {
                    const lowerQuery = searchQuery.toLowerCase();
                    fetchedBusinesses = fetchedBusinesses.filter(b =>
                        (b.name && b.name.toLowerCase().includes(lowerQuery)) ||
                        (b.description && b.description.toLowerCase().includes(lowerQuery)) ||
                        (b.address && b.address.toLowerCase().includes(lowerQuery)) ||
                        (b.owner && b.owner.toLowerCase().includes(lowerQuery))
                    );
                }

                setBusinesses(fetchedBusinesses);
                setTotalPages(data.meta?.totalPages || 1);
                setError(null);

            } catch (err) {
                console.error("Error fetching businesses:", err);
                setError(err.message);
                setBusinesses([]);
            } finally {
                setLoading(false);
            }
        }, 500); // 500ms debounce delay

        // Cleanup timeout on unmount or when dependencies change
        return () => clearTimeout(delayDebounceFn);

    }, [currentPage, searchQuery]); // Re-run fetch when page or search query changes

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        // Reset to page 1 when starting a new search
        setCurrentPage(1);
    };

    // Navigate to business detail page
    const handleBusinessClick = (id) => {
        // navigate(`/businesses/${id}`);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <>
            <Hero
                title="Clan Businesses"
                subtitle="Entrepreneurship runs in our blood"
                backgroundImage="/assets/images/logo.jpg"
            />

            <BusinessesContainer>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <Introduction>
                        <SectionTitle>Our Entrepreneurial Legacy</SectionTitle>
                        <p>
                            Entrepreneurship has been a cornerstone of our clan's identity for generations. From small shops to
                            thriving enterprises, our clan members have built businesses that not only provide for our families
                            but also contribute to our communities. These ventures reflect our values of hard work, innovation,
                            and service to others. Explore the diverse businesses established and operated by members of our clan.
                        </p>
                    </Introduction>

                    <Section>
                        <SectionTitle>Current Clan Enterprises</SectionTitle>

                        {/* Search Input Component */}
                        <SearchContainer>
                            <SearchInput
                                type="text"
                                placeholder="Search by business name, industry, or location..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                            />
                        </SearchContainer>

                        {loading && <LoadingMessage><DancingShield />Loading businesses...</LoadingMessage>}

                        {error && (
                            <ErrorMessage>
                                <DancingShield />
                                Unable to load businesses at this time. Please try again later.
                            </ErrorMessage>
                        )}

                        {!loading && !error && businesses.length === 0 && (
                            <NoResultsMessage>
                                <DancingShield />
                                No businesses found matching "{searchQuery}".
                            </NoResultsMessage>
                        )}

                        {!loading && !error && businesses.length > 0 && (
                            <>
                                <BusinessesGrid>
                                    {businesses.map(business => (
                                        <CardWrapper
                                            key={business.id}
                                            onClick={() => handleBusinessClick(business.id)}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            <BusinessCard business={business} />
                                        </CardWrapper>
                                    ))}
                                </BusinessesGrid>

                                {/* Pagination Controls */}
                                <PaginationContainer>
                                    <PageButton
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        Previous
                                    </PageButton>

                                    <span>Page {currentPage} of {totalPages}</span>

                                    <PageButton
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    >
                                        Next
                                    </PageButton>
                                </PaginationContainer>
                            </>
                        )}
                    </Section>
                </motion.div>
            </BusinessesContainer>
        </>
    );
};

export default FamilyBusinesses;